import React, { useMemo, useRef, useState } from "react";
import { currencyFormatter, formatDate } from "../../Helpers/Utils";

// Hand-built responsive SVG area chart - no charting library in this project's
// dependencies, and one line series doesn't need one. viewBox-scaled so it
// stays sharp and responsive at any width; a single measure (net revenue) is
// drawn, never a second y-axis, with captured/refunded surfaced only in the
// hover tooltip so the picture never implies two independently-scaled axes.
const WIDTH = 640;
const HEIGHT = 220;
const PAD = { top: 16, right: 12, bottom: 28, left: 54 };

const RevenueChart = ({ data, loading }) => {
    const wrapRef = useRef(null);
    const [hoverIndex, setHoverIndex] = useState(null);
    const [hoverPct, setHoverPct] = useState(0);

    const trend = useMemo(() => data?.trend || [], [data]);
    const plotWidth = WIDTH - PAD.left - PAD.right;
    const plotHeight = HEIGHT - PAD.top - PAD.bottom;

    const { points, minV, maxV } = useMemo(() => {
        if (!trend.length) return { points: [], minV: 0, maxV: 0 };
        const values = trend.map((row) => row.net);
        const rawMax = Math.max(0, ...values);
        const rawMin = Math.min(0, ...values);
        const span = rawMax - rawMin || 1;
        const stepX = trend.length > 1 ? plotWidth / (trend.length - 1) : 0;
        const pts = trend.map((row, i) => ({
            x: PAD.left + stepX * i,
            y: PAD.top + plotHeight - ((row.net - rawMin) / span) * plotHeight,
            row,
        }));
        return { points: pts, minV: rawMin, maxV: rawMax };
    }, [trend, plotWidth, plotHeight]);

    const baselineY = PAD.top + plotHeight - ((0 - minV) / ((maxV - minV) || 1)) * plotHeight;

    const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
    const areaPath = points.length
        ? `${linePath} L${points[points.length - 1].x.toFixed(1)},${(PAD.top + plotHeight).toFixed(1)} L${points[0].x.toFixed(1)},${(PAD.top + plotHeight).toFixed(1)} Z`
        : "";

    const yTicks = 4;
    const tickValues = Array.from({ length: yTicks + 1 }, (_, i) => minV + ((maxV - minV) * i) / yTicks);

    const handleMove = (event) => {
        if (!points.length || !wrapRef.current) return;
        const rect = wrapRef.current.getBoundingClientRect();
        const scaleX = WIDTH / rect.width;
        const svgX = (event.clientX - rect.left) * scaleX;
        const stepX = points.length > 1 ? plotWidth / (points.length - 1) : plotWidth;
        let index = Math.round((svgX - PAD.left) / (stepX || 1));
        index = Math.max(0, Math.min(points.length - 1, index));
        setHoverIndex(index);
        setHoverPct(((points[index].x) / WIDTH) * 100);
    };

    if (loading) {
        return <div className="chart-placeholder"><span className="skeleton" style={{ width: "60%", height: 16 }} /></div>;
    }
    if (!trend.length) {
        return (
            <div className="revenue-chart-empty">
                <strong>No revenue recorded yet</strong>
                <span className="small">Captured payments will appear here once bookings are paid for.</span>
            </div>
        );
    }

    const hovered = hoverIndex !== null ? points[hoverIndex] : null;

    return (
        <div>
            <div className="db-summary-line mb-2">
                Net revenue, last {data?.days || trend.length} days: <strong>{currencyFormatter(data?.totalNet || 0)}</strong>
            </div>
            <div className="revenue-chart-wrap" ref={wrapRef} onMouseMove={handleMove} onMouseLeave={() => setHoverIndex(null)}>
                <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img" aria-label="Net revenue trend over time">
                    <defs>
                        <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="var(--chart-fill-from)" />
                            <stop offset="100%" stopColor="var(--chart-fill-to)" />
                        </linearGradient>
                    </defs>

                    {tickValues.map((tick, i) => {
                        const y = PAD.top + plotHeight - ((tick - minV) / ((maxV - minV) || 1)) * plotHeight;
                        return (
                            <g key={i}>
                                <line x1={PAD.left} x2={WIDTH - PAD.right} y1={y} y2={y} stroke="var(--chart-grid)" strokeWidth="1" />
                                <text x={PAD.left - 8} y={y + 4} textAnchor="end" fontSize="10" fill="var(--chart-axis-text)">
                                    {currencyFormatter(tick).replace("₹", "")}
                                </text>
                            </g>
                        );
                    })}

                    <line x1={PAD.left} x2={WIDTH - PAD.right} y1={baselineY} y2={baselineY} stroke="var(--chart-axis-text)" strokeOpacity="0.35" strokeDasharray="3 3" />

                    {areaPath ? <path d={areaPath} fill="url(#revenueFill)" /> : null}
                    {linePath ? <path d={linePath} fill="none" stroke="var(--chart-line)" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" /> : null}

                    {points.map((p, i) => (
                        <React.Fragment key={i}>
                            {i % Math.ceil(points.length / 6 || 1) === 0 || i === points.length - 1 ? (
                                <text x={p.x} y={HEIGHT - 8} textAnchor="middle" fontSize="10" fill="var(--chart-axis-text)">
                                    {formatDate(p.row.date)}
                                </text>
                            ) : null}
                        </React.Fragment>
                    ))}

                    {hovered ? (
                        <g>
                            <line x1={hovered.x} x2={hovered.x} y1={PAD.top} y2={PAD.top + plotHeight} stroke="var(--chart-line)" strokeOpacity="0.25" />
                            <circle cx={hovered.x} cy={hovered.y} r="4.5" fill="#fff" stroke="var(--chart-line)" strokeWidth="2" />
                        </g>
                    ) : null}
                </svg>

                {hovered ? (
                    <div className="revenue-chart-tooltip" style={{ left: `${hoverPct}%` }}>
                        <strong>{formatDate(hovered.row.date)}</strong>
                        <br />Captured: {currencyFormatter(hovered.row.captured)}
                        <br />Refunded: {currencyFormatter(hovered.row.refunded)}
                        <br />Net: {currencyFormatter(hovered.row.net)}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default RevenueChart;
